<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog as="div" class="tw-wrapper tw-relative" @close="emit('close')">
      <TransitionChild
        as="template"
        enter="tw-ease-out tw-duration-300"
        enter-from="tw-opacity-0"
        enter-to="tw-opacity-100"
        leave="tw-ease-in tw-duration-200"
        leave-from="tw-opacity-100"
        leave-to="tw-opacity-0"
      >
        <div class="tw-fixed tw-inset-0 tw-z-50 tw-bg-gray-500 tw-bg-opacity-75 tw-transition-opacity" />
      </TransitionChild>

      <div class="tw-fixed tw-inset-0 tw-z-50 sm:tw-overflow-y-auto">
        <div class="tw-flex tw-h-full tw-items-center tw-justify-center sm:tw-h-auto sm:tw-min-h-full">
          <TransitionChild
            as="template"
            enter="tw-ease-out tw-duration-300"
            enter-from="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
            enter-to="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
            leave="tw-ease-in tw-duration-200"
            leave-from="tw-opacity-100 tw-translate-y-0 sm:tw-scale-100"
            leave-to="tw-opacity-0 tw-translate-y-4 sm:tw-translate-y-0 sm:tw-scale-95"
          >
            <DialogPanel
              class="tw-relative tw-my-2 tw-h-[calc(100%-16px)] tw-w-[calc(100%-16px)] tw-transform tw-overflow-hidden tw-rounded-lg tw-shadow-xl tw-transition-all sm:tw-my-8 sm:tw-h-auto sm:tw-w-[90%] sm:tw-max-w-screen-lg sm:tw-p-6"
              :class="loanType === 'bridging' ? 'tw-bg-bridgit-mayaBlue-25' : 'tw-bg-bridgit-lightBlue'"
            >
              <div class="tw-absolute tw-right-0 tw-top-0 tw-text-white sm:tw-text-inherit">
                <button
                  type="button"
                  class="tw-rounded-md tw-p-4 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-bridgit-lightBlue focus-visible:tw-ring-offset-2"
                  @click="emit('close')"
                >
                  <span class="tw-sr-only">Close</span>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M18.6663 9.01316L15.0027 12.6767L11.3371 9.01107C10.6886 8.36259 9.63771 8.36229 8.98959 9.01041C8.34148 9.65852 8.34178 10.7094 8.99026 11.3579L12.6559 15.0236L8.99234 18.6871C8.34423 19.3352 8.34452 20.3862 8.99301 21.0346C9.64149 21.6831 10.6856 21.6766 11.3337 21.0285L14.9973 17.3649L18.6629 21.0306C19.3114 21.679 20.3623 21.6793 21.0104 21.0312C21.6585 20.3831 21.6582 19.3322 21.0097 18.6837L17.3441 15.0181L21.0077 11.3545C21.6558 10.7064 21.6555 9.65548 21.0138 9.01382C20.3722 8.37217 19.3144 8.36504 18.6663 9.01316Z"
                      fill="currentColor"
                    />
                    <rect x="0.5" y="0.5" width="29" height="29" rx="4.5" stroke="currentColor" />
                  </svg>
                </button>
              </div>
              <div class="tw-flex tw-h-full tw-flex-col sm:tw-h-auto">
                <div class="tw-bg-bridgit-navy tw-p-4 tw-text-white sm:tw-bg-inherit sm:tw-text-inherit">
                  <DialogTitle
                    as="h3"
                    class="tw-mr-[46px] tw-text-2xl tw-font-bold sm:tw-mr-0 md:tw-text-3xl lg:tw-text-4xl"
                  >
                    {{ loanType === 'bridging' ? 'Bridging loan details' : 'Unlockit loan details' }}
                  </DialogTitle>
                </div>
                <div class="tw-flex-1 tw-overflow-y-auto tw-p-4 sm:tw-overflow-y-visible sm:tw-pt-0">
                  <div class="tw-text-bridgit-darkGrey">
                    Below is a snapshot of what your scenario could look like with a 12-month residential bridging loan.
                  </div>
                  <div class="tw-mt-4 tw-rounded-xl tw-border tw-border-bridgit-darkGrey tw-p-4">
                    <div class="tw-grid tw-grid-cols-1" :class="showLoanInfo ? 'md:tw-grid-cols-2' : ''">
                      <div
                        class="md:tw-pr-4"
                        :class="showLoanInfo ? 'md:tw-border-r md:tw-border-r-bridgit-lightGrey' : ''"
                      >
                        <div class="tw-text-xl">
                          Your estimated principal is
                          <span class="tw-font-bold"> {{ formatValue(totalLoanAmount) }}&nbsp; </span>
                          <div v-if="!showLoanInfo" class="tw-inline-flex tw-justify-center tw-font-bold">
                            (LVR&nbsp;{{ lvr ? lvr.toFixed(0) : '' }}%)
                            <div
                              v-tippy="
                                'Loan to Value Ratio (LVR) is the amount of money you are borrowing shown as a percentage. This is based on the combined value of the property you are buying and the property you are selling.'
                              "
                              class="tw-ml-2 tw-flex tw-items-start"
                            >
                              <InformationCircleIcon class="tw-h-6 tw-w-6 tw-cursor-pointer" aria-hidden="true" />
                            </div>
                          </div>
                        </div>
                        <div v-if="!showLoanInfo" class="tw-mt-4">
                          Based on the information provided your Loan to Value Ratio (LVR) may be too high. To help you
                          we require some additional information; feel free to apply now or schedule a call with our
                          team.
                        </div>
                        <div class="tw-mb-2 tw-mt-4 tw-text-xl tw-font-bold tw-text-bridgit-darkGrey">Summary</div>
                        <div class="tw-grid tw-grid-cols-[1fr_auto] tw-gap-1">
                          <div class="tw-text-bridgit-darkGey">
                            {{ loanType === 'bridging' ? 'Purchase price' : 'Funds requested' }}
                          </div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ formatValue(newLoanAmount) }}
                          </div>
                          <div class="tw-text-bridgit-darkGey">Refinance existing mortgage</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ formatValue(existingMortgage) }}
                          </div>
                          <div v-if="loanType === 'bridging'" class="tw-text-bridgit-darkGey">
                            Less your contribution
                          </div>
                          <div v-if="loanType === 'bridging'" class="tw-justify-self-end tw-font-bold">
                            {{ formatValue(deposit ? deposit * -1 : undefined) }}
                          </div>
                          <div class="tw-text-bridgit-darkGey tw-pt-1 tw-font-bold">Total</div>
                          <div class="tw-justify-self-end tw-pt-1 tw-font-bold">
                            {{ formatValue(totalLoanAmount) }}
                          </div>
                        </div>
                      </div>
                      <div v-if="showLoanInfo" class="tw-mt-4 md:tw-mt-0 md:tw-pl-4">
                        <div class="tw-mb-2 tw-text-xl tw-font-bold tw-text-bridgit-darkGrey">Loan information</div>
                        <div class="tw-grid tw-grid-cols-[1fr_auto] tw-gap-1">
                          <div class="tw-text-bridgit-darkGey">Example set-up fee</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ loanInfo.setupFeeSold }}
                          </div>
                          <div class="tw-text-bridgit-darkGey">Loan term*</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ loanInfo.loanTerm }}
                          </div>
                          <div class="tw-text-bridgit-darkGey">Variable Bridge Rate</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ loanInfo.variableBridgeRate }}
                          </div>
                          <div class="tw-text-bridgit-darkGey">Comparison rate^</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ loanInfo.comparisonRate }}
                          </div>
                          <div class="tw-text-bridgit-darkGey">Monthly repayments</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ loanInfo.monthlyPayment }}
                          </div>
                          <div class="tw-text-bridgit-darkGey">LVR</div>
                          <div class="tw-justify-self-end tw-font-bold">
                            {{ loanInfo.lvr }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="tw-mt-4 tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center">
                      <Button variant="primary" :link-href="getAQuoteUrl" link-target="_blank">
                        <span class="tw-font-bold">Get a quote</span>
                      </Button>
                      <Button variant="link" :link-href="scheduleCallUrl" link-target="_blank">
                        <span class="tw-font-bold">Schedule a call</span>
                      </Button>
                    </div>
                  </div>
                  <div class="tw-mt-4 tw-text-bridgit-navy">
                    <div
                      class="tw-flex tw-cursor-pointer tw-items-center tw-font-bold"
                      @click="isDisclaimerOpen = !isDisclaimerOpen"
                    >
                      Disclaimers
                      <ChevronDownIcon
                        class="tw-ml-1 tw-h-5 tw-w-5 tw-transform tw-text-bridgit-navy tw-transition-transform"
                        :class="isDisclaimerOpen ? 'tw-rotate-180' : ''"
                      />
                    </div>
                    <div v-show="isDisclaimerOpen" id="disclaimer-section">
                      <p class="tw-mt-2 tw-text-sm">
                        This calculator provides an estimate only and is based on a residential bridging loan with a
                        12-month loan term. The calculator is not an offer of credit or an approval. Loan information
                        and structure may change once you complete the application and a credit assessment is complete.
                        Eligibility and approval are subject to standard credit assessment, and not all amounts, term
                        lengths or rates will be available to all applicants. Fees, terms and conditions apply.
                      </p>
                      <p class="tw-mt-2 tw-text-sm">
                        ^Comparison rate is calculated on a $150,000 secured loan over a 25-year term. For Upsizer
                        loans, a Bridge Rate applies for the first 12 months, followed by a Stay Rate thereafter. For
                        Downsizer loans, only the Bridge Rate applies. WARNING: This comparison rate is true only for
                        the example provided and may not include all fees and charges. Different loan amounts, terms, or
                        fee structures will result in different comparison rates. For interest-only periods, your loan
                        balance does not reduce, meaning you may pay more interest over the life of the loan. Set-up fee
                        from {{ loanInfo.setupFeeSold }} and government charges apply.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { LoanType } from '@/helpers/common';
import { TippyDirective } from '@/helpers/tippy';
import Button from './Button.vue';

const isDisclaimerOpen = ref(false);

const scheduleCallUrl = import.meta.env.VITE_SCHEDULE_CALL_URL;
const getAQuoteUrl = import.meta.env.VITE_GET_A_QUOTE_URL;
const vTippy = TippyDirective;

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    loanType: LoanType;
    newLoanAmount?: number;
    deposit?: number;
    existingPropertyValue?: number;
    existingMortgage?: number;
    totalLoanAmount: number;
    lvr?: number;
    applyNowUrl?: string;
  }>(),
  {
    newLoanAmount: undefined,
    deposit: undefined,
    existingPropertyValue: undefined,
    existingMortgage: undefined,
    lvr: undefined,
    applyNowUrl: import.meta.env.VITE_APPLY_URL,
  },
);

const showLoanInfo = computed(() => !props.lvr || props.lvr <= 75);

const loanInfo = computed(() => {
  return props.loanType === 'bridging'
    ? {
        setupFeeSold: '0.60%',
        setupFeeNotSold: '1.25%',
        interestFreePeriod: '1 month',
        loanTerm: '12 months',
        interestRate: '8.45%',
        comparisonRateSold: '8.57%',
        comparisonRateNotSold: '8.60%',
        monthlyPayment: '-',
        lvr: props.lvr ? `${props.lvr.toFixed(2)}%` : '-',
        introductoryInterestRate: '8.49%',
        bridgingInterestRate: '9.99%',
        comparisonRate: '7.43% ',
        variableBridgeRate: '8.49%',
      }
    : {
        setupFee: '1.5%',
        interestFreePeriod: '2 months',
        loanTerm: '6 months',
        interestRate: props.totalLoanAmount >= 500000 ? '0.67% p.m.' : '0.85% p.m.',
        comparisonRate: props.totalLoanAmount >= 500000 ? '8.21%' : '10.39%',
        monthlyPayment: '$0',
        lvr: props.lvr ? `${props.lvr.toFixed(2)}%` : '-',
        introductoryInterestRate: '8.99%',
        variableBridgeRate: '7.99%',
      };
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const formatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatValue(value?: number, absoluteValue = false) {
  if (value === undefined) {
    return '$ -';
  }
  return absoluteValue ? formatter.format(Math.abs(value)) : formatter.format(value);
}
</script>
